import axios from 'axios';
import { update } from '../services/item';
import { deepCloneArray } from './array';

export {
  canAccessItem,
  canEditItemForItem,
  getEffectiveRoleForItem,
  isItemCampaignAccessible,
  isTabCampaignAccessible,
  validPassword,
} from './permissions';

export const getData = element => element[Object.keys(element)];
export const getId = element => Object.keys(element)[0];
export const isBuiltinTabId = tabId =>
  tabId === 'text' ||
  tabId === 'img' ||
  tabId === 'link' ||
  tabId === 'num' ||
  tabId === 'conditions';

const buildPicEntry = picObj => {
  if (!picObj) {
    return null;
  }
  const id = getId(picObj);
  if (!id) {
    return null;
  }
  const payload = picObj[id] || {};
  return {
    id,
    ...payload,
    url: payload?.url ?? null,
    iconSlug: payload?.iconSlug ?? null,
    bgColor: payload?.bgColor ?? null,
  };
};

export const getPicEntryById = (pics = [], picId) => {
  if (!picId || !Array.isArray(pics)) {
    return null;
  }
  const target = pics.find(picObj => getId(picObj) === picId);
  return buildPicEntry(target);
};

export const getPicEntryAtIndex = (pics = [], index = 0) => {
  if (!Array.isArray(pics) || index < 0 || index >= pics.length) {
    return null;
  }
  return buildPicEntry(pics[index]);
};

export const getFirstPicEntry = pics => {
  if (!Array.isArray(pics) || pics.length === 0) {
    return null;
  }
  return buildPicEntry(pics[0]);
};

export const resolvePicAsset = entry => {
  if (!entry) {
    return { url: null, iconSlug: null, id: null, bgColor: null };
  }
  const { url = null, iconSlug = null, id = null, bgColor = null } = entry;
  return { url, iconSlug, id, bgColor };
};

export const getItemPicAsset = (item, index = 0) => {
  if (!item?.pics) {
    return { url: null, iconSlug: null, id: null, bgColor: null };
  }
  const entry = getPicEntryAtIndex(item.pics, index);
  return resolvePicAsset(entry);
};

export const getAvatarBackgroundStyle = color => {
  if (!color) {
    return {};
  }
  if (color === 'transparent') {
    return { backgroundColor: 'transparent' };
  }
  return { backgroundColor: `rgb(var(--${color}))` };
};

// LINKS
export const addLink = async (item, itemInList, callback) => {
  const i = itemInList._id;
  const linkUrl = `items/${i}`;
  let alreadyExistFromLink = false;
  for (const objLink of itemInList.links) {
    alreadyExistFromLink = getId(objLink) === item._id;
  }
  let alreadyExistFromCurrent = false;
  for (const objLink of item.links) {
    alreadyExistFromCurrent = getId(objLink) === itemInList._id;
  }

  if (alreadyExistFromCurrent === false) {
    addLinkToCurrent(itemInList, item);
  }
  if (alreadyExistFromLink === false) {
    addLinkToLink(linkUrl, item);
  }
  await syncAutoTabsOnLinkAdded(item, itemInList);
  if (callback) {
    callback();
  }
};

export const addLinkToCurrent = (itemInList, item) => {
  const clonedLinks = deepCloneArray(item.links);
  const i = itemInList._id;
  clonedLinks.push({ [i]: { id: i, x: 0, y: 0, w: 4, h: 1, i: i } });
  update(`items/${item._id}`, 'links', clonedLinks);
};

export const addLinkToLink = async (linkUrl, item) => {
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/${linkUrl}`);
  const cloneLinkedLink = deepCloneArray(res.data.links);
  cloneLinkedLink.push({
    [item._id]: { id: item._id, x: 0, y: 0, w: 4, h: 1, i: item._id },
  });
  update(linkUrl, 'links', cloneLinkedLink);
};

export const deleteLink = async (item, linkId) => {
  const clonedLinks = deepCloneArray(item.links);
  let idx = -1;
  for (let i = 0; i < clonedLinks.length; i++) {
    const text = clonedLinks[i];
    if (Object.keys(text)[0] === linkId[0] || Object.keys(text)[0] === linkId) {
      idx = i;
    }
  }

  if (idx > -1) {
    clonedLinks.splice(idx, 1);
    update(`items/${item._id}`, 'links', clonedLinks);
  }

  const linkUrl = `items/${linkId}`;
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/${linkUrl}`);
  const cloneLinkedLink = deepCloneArray(res.data.links);
  let idxLink = -1;
  for (let i = 0; i < cloneLinkedLink.length; i++) {
    const text = cloneLinkedLink[i];
    if (Object.keys(text)[0] === item._id) {
      idxLink = i;
    }
  }
  if (idxLink > -1) {
    cloneLinkedLink.splice(idxLink, 1);
    update(linkUrl, 'links', cloneLinkedLink);
  }

  await syncAutoTabsOnLinkRemoved(item, res.data);
};

const getAutoLinkedTabEntries = sourceItem => {
  if (!Array.isArray(sourceItem?.tabs)) {
    return [];
  }
  return sourceItem.tabs
    .filter(tabObj => {
      const id = getId(tabObj);
      const payload = tabObj?.[id];
      return payload?.addToLinkedItems === true;
    })
    .map(tabObj => {
      const id = getId(tabObj);
      return { id, payload: tabObj?.[id] || {} };
    })
    .filter(entry => Boolean(entry.id));
};

const upsertTabsToItem = async (targetItem, tabEntries = []) => {
  if (!targetItem?._id || tabEntries.length === 0) {
    return;
  }
  const currentTabs = Array.isArray(targetItem.tabs)
    ? deepCloneArray(targetItem.tabs)
    : [];
  let changed = false;
  tabEntries.forEach(entry => {
    const exists = currentTabs.some(tabObj => getId(tabObj) === entry.id);
    if (exists) {
      return;
    }
    const copiedPayload = { ...entry.payload };
    delete copiedPayload.addToLinkedItems;
    currentTabs.push({ [entry.id]: copiedPayload });
    changed = true;
  });
  if (changed) {
    await update(`items/${targetItem._id}`, 'tabs', currentTabs);
  }
};

const removeTabsFromItem = async (targetItem, tabIds = []) => {
  if (!targetItem?._id || tabIds.length === 0) {
    return;
  }
  const currentTabs = Array.isArray(targetItem.tabs)
    ? deepCloneArray(targetItem.tabs)
    : [];
  const nextTabs = currentTabs.filter(tabObj => !tabIds.includes(getId(tabObj)));
  if (nextTabs.length !== currentTabs.length) {
    await update(`items/${targetItem._id}`, 'tabs', nextTabs);
    if (tabIds.includes(targetItem?.sidebar)) {
      await update(`items/${targetItem._id}`, 'sidebar', null);
    }
  }
};

export const syncAutoTabsOnLinkAdded = async (itemA, itemB) => {
  const aEntries = getAutoLinkedTabEntries(itemA);
  const bEntries = getAutoLinkedTabEntries(itemB);
  await Promise.all([
    upsertTabsToItem(itemB, aEntries),
    upsertTabsToItem(itemA, bEntries),
  ]);
};

export const syncAutoTabsOnLinkRemoved = async (itemA, itemB) => {
  const aTabIds = getAutoLinkedTabEntries(itemA).map(entry => entry.id);
  const bTabIds = getAutoLinkedTabEntries(itemB).map(entry => entry.id);
  await Promise.all([
    removeTabsFromItem(itemB, aTabIds),
    removeTabsFromItem(itemA, bTabIds),
  ]);
};

